import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase }              from '@/shared/lib/supabase';
import { polaroidRepository }    from '../../data/polaroidRepository';
import type { InteracaoPolaroid } from '@/shared/types';
import type {
  RealtimeChannel,
  RealtimePostgresInsertPayload,
} from '@supabase/supabase-js';

export function useModeracaoPolaroids(empresaId: string | undefined) {
  const [polaroids, setPolaroids] = useState<InteracaoPolaroid[]>([]);
  const [loading,   setLoading]   = useState(true);

  const channelRef = useRef<RealtimeChannel | null>(null);
  const onInsertRef = useRef<(p: RealtimePostgresInsertPayload<InteracaoPolaroid>) => void>(undefined);

  const carregarPendentes = useCallback(async () => {
    if (!empresaId) { setLoading(false); return; }
    setLoading(true);
    try {
      const lista = await polaroidRepository.listarPendentes(empresaId);
      setPolaroids(lista);
    } catch {
      setPolaroids([]);
    } finally {
      setLoading(false);
    }
  }, [empresaId]);

  useEffect(() => { carregarPendentes(); }, [carregarPendentes]);

  // Padrão cbRef — evita stale closure no handler Realtime
  useEffect(() => {
    onInsertRef.current = (payload) => {
      const nova = payload.new as InteracaoPolaroid;
      if (nova.status === 'pendente') {
        setPolaroids(prev => [nova, ...prev]);
      }
    };
  });

  useEffect(() => {
    if (!empresaId) return;

    const canal = supabase
      .channel(`moderacao_polaroids_${empresaId}`)
      .on<InteracaoPolaroid>(
        'postgres_changes',
        {
          event:  'INSERT',
          schema: 'public',
          table:  'interacao_polaroids',
          filter: `empresa_id=eq.${empresaId}`,
        },
        (payload) => {
          onInsertRef.current?.(
            payload as RealtimePostgresInsertPayload<InteracaoPolaroid>,
          );
        },
      )
      .subscribe();

    channelRef.current = canal;
    return () => { supabase.removeChannel(canal); };
  }, [empresaId]);

  const aprovar = useCallback(async (id: string): Promise<void> => {
    if (!empresaId) return;
    await polaroidRepository.atualizarStatus(empresaId, id, 'aprovada');
    setPolaroids(prev => prev.filter(p => p.id !== id));
  }, [empresaId]);

  const rejeitar = useCallback(async (id: string): Promise<void> => {
    if (!empresaId) return;
    await polaroidRepository.atualizarStatus(empresaId, id, 'rejeitada');
    setPolaroids(prev => prev.filter(p => p.id !== id));
  }, [empresaId]);

  return {
    polaroids,
    loading,
    aprovar,
    rejeitar,
    recarregar: carregarPendentes,
  };
}
